import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarPlus, Download, Search, X } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, APPLICATION_STATUS_LABELS, EmptyState, formatDate, inputClass, Label, PrimaryButton, SecondaryButton, Spinner, StatusPill } from "@/components/employer/portal/shared";

const STATUSES = ["submitted", "under_review", "shortlisted", "interview", "selected", "offer", "documentation", "visa_processing", "placement", "rejected"];
const FIELDS = [
    ["email", "Email"], ["phone", "Phone / WhatsApp"], ["country_of_residence", "Country"],
    ["nationality", "Nationality"], ["current_location", "Current location"], ["profession", "Profession"],
    ["years_experience", "Experience"], ["highest_qualification", "Education"], ["certifications", "Certifications"],
    ["preferred_destination", "Preferred destination"],
];

export default function EmployerApplicants({ fixedStatus = "" }) {
    const [items, setItems] = useState(null);
    const [filters, setFilters] = useState({ q: "", status: fixedStatus });
    const [selected, setSelected] = useState(null);
    const [interview, setInterview] = useState({ scheduled_at: "", mode: "Video call", location: "", notes: "" });
    const [params] = useSearchParams();

    const load = useCallback(async () => {
        const query = new URLSearchParams();
        if (filters.q) query.set("q", filters.q);
        if (filters.status) query.set("status", filters.status);
        const res = await apiFetch(`/employer/applicants?${query}`);
        if (res.ok) setItems(await res.json());
    }, [filters]);

    useEffect(() => {
        const t = setTimeout(load, 250);
        return () => clearTimeout(t);
    }, [load]);

    const open = async (id) => {
        const res = await apiFetch(`/employer/applicants/${id}`);
        if (res.ok) setSelected(await res.json());
    };

    useEffect(() => {
        const id = params.get("open");
        if (id) open(id);
    }, [params]);

    const updateStatus = async (id, status) => {
        const res = await apiFetch(`/employer/applicants/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status }),
        });
        if (res.ok) {
            setItems((prev) => prev?.map((item) => (item.id === id ? { ...item, status } : item)) || []);
            setSelected((prev) => (prev ? { ...prev, status } : prev));
        }
    };

    const schedule = async () => {
        if (!selected || !interview.scheduled_at) return;
        const res = await apiFetch(`/employer/applicants/${selected.id}/interviews`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(interview),
        });
        if (res.ok) {
            setSelected((prev) => ({ ...prev, status: "interview" }));
            setItems((prev) => prev.map((item) => (item.id === selected.id ? { ...item, status: "interview" } : item)));
            setInterview({ scheduled_at: "", mode: "Video call", location: "", notes: "" });
        }
    };

    const download = async (stored, original) => {
        const res = await apiFetch(`/employer/applicants/${selected.id}/files/${stored}`);
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = original || "document";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <EmployerLayout
            title={fixedStatus === "shortlisted" ? "Shortlisted Candidates" : "Applicants"}
            subtitle="Review only candidates who applied to your own vacancies, move them through the pipeline, and schedule interviews."
        >
            <div className="mb-7 flex flex-col gap-3 sm:flex-row" data-testid="applicant-filters">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/35" />
                    <input data-testid="applicant-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search candidates, roles, professions…" className={`${inputClass} pl-11`} />
                </div>
                {!fixedStatus && (
                    <select data-testid="applicant-status-filter" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={`${inputClass} cursor-pointer sm:w-64`}>
                        <option value="">All statuses</option>
                        {STATUSES.map((status) => <option key={status} value={status}>{APPLICATION_STATUS_LABELS[status]}</option>)}
                    </select>
                )}
            </div>

            {!items ? <Spinner /> : items.length === 0 ? (
                <EmptyState title="No applicants yet" copy="Candidates who apply to your published vacancies will appear here with their application details." />
            ) : (
                <div data-testid="employer-applicants-list" className="border-t border-ink/10">
                    {items.map((item) => (
                        <button key={item.id} type="button" data-testid={`applicant-row-${item.id}`} onClick={() => open(item.id)} className="group grid w-full grid-cols-[1fr_auto] items-center gap-5 border-b border-ink/10 px-2 py-5 text-left transition-colors hover:bg-sand lg:grid-cols-[1fr_auto_auto]">
                            <span className="min-w-0">
                                <span className="block truncate font-serif text-xl font-medium tracking-tight text-ink group-hover:text-forest">{item.full_name}</span>
                                <span className="mt-1 block truncate text-xs text-ink/50">{item.job_title} · {item.profession} · {item.country_of_residence} · {formatDate(item.created_at)}</span>
                            </span>
                            <span className="hidden text-xs text-ink/45 lg:block">{item.reference}</span>
                            <StatusPill status={item.status} type="application" />
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-50" data-testid="applicant-detail">
                    <div className="absolute inset-0 bg-night/55" onClick={() => setSelected(null)} />
                    <aside className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto bg-ivory p-7 shadow-2xl sm:p-10">
                        <div className="flex items-start justify-between gap-6">
                            <div>
                                <StatusPill status={selected.status} type="application" />
                                <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight">{selected.full_name}</h2>
                                <p className="mt-2 text-sm text-ink/55">{selected.job_title} · Ref {selected.reference}</p>
                            </div>
                            <button type="button" data-testid="applicant-close" onClick={() => setSelected(null)} aria-label="Close applicant" className="border border-ink/15 p-2 text-ink/60 hover:border-forest hover:text-forest"><X size={18} /></button>
                        </div>

                        <div className="mt-8 border-t border-ink/10">
                            {FIELDS.map(([key, label]) => selected[key] ? (
                                <div key={key} className="grid grid-cols-[150px_1fr] gap-4 border-b border-ink/10 py-3">
                                    <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/45">{label}</span>
                                    <span className="break-words text-sm text-ink/75">{selected[key]}</span>
                                </div>
                            ) : null)}
                        </div>

                        {selected.cover_letter && (
                            <div className="mt-7">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/45">Cover message</p>
                                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-ink/70">{selected.cover_letter}</p>
                            </div>
                        )}

                        <div className="mt-7 flex flex-wrap gap-2">
                            {selected.cv?.stored && <SecondaryButton data-testid="applicant-download-cv" onClick={() => download(selected.cv.stored, selected.cv.original)}><Download size={14} /> CV</SecondaryButton>}
                            {(selected.documents || []).map((doc) => <SecondaryButton key={doc.stored} data-testid={`applicant-doc-${doc.stored}`} onClick={() => download(doc.stored, doc.original)}><Download size={14} /> {doc.original}</SecondaryButton>)}
                        </div>

                        <div className="mt-9">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/45">Application status</p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {STATUSES.filter((status) => status !== selected.status).map((status) => (
                                    <button key={status} type="button" data-testid={`applicant-status-${status}`} onClick={() => updateStatus(selected.id, status)} className="border border-forest/25 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors hover:bg-forest hover:text-ivory">
                                        {APPLICATION_STATUS_LABELS[status]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-9 border border-ink/10 bg-sand p-6">
                            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-forest"><CalendarPlus size={15} /> Schedule interview</p>
                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                <div><Label>Date & time</Label><input data-testid="interview-datetime" type="datetime-local" value={interview.scheduled_at} onChange={(e) => setInterview({ ...interview, scheduled_at: e.target.value })} className={inputClass} /></div>
                                <div><Label>Mode</Label><input data-testid="interview-mode" value={interview.mode} onChange={(e) => setInterview({ ...interview, mode: e.target.value })} className={inputClass} /></div>
                                <div className="sm:col-span-2"><Label>Location / link</Label><input data-testid="interview-location" value={interview.location} onChange={(e) => setInterview({ ...interview, location: e.target.value })} className={inputClass} /></div>
                                <div className="sm:col-span-2"><Label>Notes</Label><textarea data-testid="interview-notes" rows={3} value={interview.notes} onChange={(e) => setInterview({ ...interview, notes: e.target.value })} className={`${inputClass} resize-none`} /></div>
                            </div>
                            <PrimaryButton data-testid="schedule-interview" onClick={schedule} className="mt-5">Schedule Interview</PrimaryButton>
                        </div>
                    </aside>
                </div>
            )}
        </EmployerLayout>
    );
}
